import { useCallback, useEffect, useState } from 'react';
import { once, run } from '../helper/electron';
import AppleMusicService from '../services/AppleMusic.service';
import SpotifyService from '../services/Spotify.service';
import TidalService from '../services/Tidal.service';
import { useDispatch, useSelector } from '../store/hooks';
import { AppIdentifiers, setToken } from '../store/slices/token-state.slice';
import isElectron from '../utils/isElectron';

export type StreamingServiceId = 'spotify' | 'tidal' | 'apple';

export type StreamingConnection = {
  id: StreamingServiceId;
  name: string;
  connected: boolean;
};

const NAMES: Record<StreamingServiceId, string> = {
  spotify: 'Spotify',
  tidal: 'Tidal',
  apple: 'Apple Music',
};

export const listStreamingConnections = (connected: {
  spotify: boolean;
  tidal: boolean;
  apple: boolean;
}): StreamingConnection[] =>
  (['spotify', 'tidal', 'apple'] as StreamingServiceId[]).map((id) => ({
    id,
    name: NAMES[id],
    connected: connected[id],
  }));

export const useStreamingConnections = () => {
  const dispatch = useDispatch();
  const spotifyToken = useSelector(
    (state) => state.tokenState.spotify?.accessToken,
  );
  const tidalToken = useSelector(
    (state) => state.tokenState.tidal?.accessToken,
  );
  const [appleConnected, setAppleConnected] = useState(
    AppleMusicService.isAuthorized(),
  );

  useEffect(() => {
    const refresh = () => setAppleConnected(AppleMusicService.isAuthorized());
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 4000);
    return () => {
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, []);

  const connections = listStreamingConnections({
    spotify: !!spotifyToken,
    tidal: !!tidalToken || TidalService.isConnected(),
    apple: appleConnected,
  });

  const connect = useCallback(
    async (serviceId: StreamingServiceId): Promise<string> => {
      if (serviceId === 'spotify') {
        const authUrl = await SpotifyService.getSpotifyAuthorizeUrl(
          window.location.hash,
        );
        if (!isElectron()) {
          window.location.href = authUrl;
          return 'Redirecting to Spotify…';
        }
        run('loginSpotify', {
          authUrl,
          redirectUri: 'cratehackersx://spotify/callback',
        });
        const result = await new Promise<{ code?: string; error?: string }>(
          (resolve) => once('loginSpotify', resolve),
        );
        if (result.error || !result.code) {
          throw new Error('Spotify authentication failed');
        }
        const tokenResponse = await SpotifyService.getToken(result.code);
        if (!tokenResponse.access_token) {
          throw new Error(
            tokenResponse.error_description || 'Failed to get Spotify token',
          );
        }
        dispatch(
          setToken(
            AppIdentifiers.Spotify,
            tokenResponse.access_token,
            tokenResponse.refresh_token,
            Date.now() + tokenResponse.expires_in * 1000,
          ),
        );
        return 'Connected to Spotify';
      }

      if (serviceId === 'tidal') {
        const authData = await TidalService.getTidalAuthData(
          window.location.hash,
        );
        if (!isElectron()) {
          window.location.href = authData.authUrl;
          return 'Redirecting to Tidal…';
        }
        run('loginTidal', {
          authUrl: authData.authUrl,
          redirectUri: authData.redirectUri,
        });
        const result = await new Promise<{
          tokens?: {
            accessToken: string;
            refreshToken: string;
            expiresIn: number;
          };
          code?: string;
          codeVerifier?: string;
          error?: string;
        }>((resolve) => once('loginTidal', resolve));
        if (result.error) throw new Error('Tidal authentication failed');

        let tokens = result.tokens;
        if (result.code && !tokens) {
          const tokenResponse = await TidalService.getToken(
            result.code,
            result.codeVerifier,
          );
          const accessToken =
            tokenResponse.accessToken || tokenResponse.access_token;
          const refreshToken =
            tokenResponse.refreshToken || tokenResponse.refresh_token;
          const expiresIn = tokenResponse.expiresIn || tokenResponse.expires_in;
          if (!accessToken) throw new Error('Failed to get Tidal token');
          tokens = { accessToken, refreshToken, expiresIn };
        }
        if (!tokens) throw new Error('Failed to get Tidal token');

        const expiry = Date.now() + tokens.expiresIn * 1000;
        dispatch(
          setToken(
            AppIdentifiers.Tidal,
            tokens.accessToken,
            tokens.refreshToken,
            expiry,
          ),
        );
        return 'Connected to Tidal';
      }

      if (isElectron()) {
        const developerToken = await AppleMusicService.fetchDeveloperToken();
        run('loginAppleMusic', { developerToken });
        const result = await new Promise<{
          userToken?: string;
          error?: string;
        }>((resolve) => once('loginAppleMusic', resolve));
        if (result.error || !result.userToken) {
          throw new Error('Apple Music authentication failed');
        }
        await AppleMusicService.setUserToken(result.userToken);
      } else {
        const authorized = await AppleMusicService.authorize();
        if (!authorized) throw new Error('Apple Music authorization failed');
      }
      setAppleConnected(true);
      return 'Connected to Apple Music';
    },
    [dispatch],
  );

  return { connections, connect };
};
